import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'

type ActivateUserParams = {
  token: string
}

type ActivateUserResponse = {
  data: {
    newPasswordToken: string
  }
}

async function activateUser({ token }: ActivateUserParams) {
  return api.backend.post('/user/activate-user', undefined, {
    headers: { 'verify-token': token },
  })
}

export function useActivateUser() {
  const { t } = useTranslation('login')
  const { mutateAsync, isPending, isSuccess, isError } = useMutation<
    ActivateUserResponse,
    AxiosError<ApiError>,
    ActivateUserParams
  >({
    mutationKey: ['/user/activate-user'],
    mutationFn: activateUser,
    onSuccess: () => {
      toast(Toast, {
        toastId: 'activate-user-success',
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t('activate_user.toast.success.title'),
          description: t('activate_user.toast.success.message'),
        },
      })
    },
    onError: (error) => {
      toast(Toast, {
        toastId: 'activate-user-error',
        data: {
          type: 'danger',
          icon: TriangleAlert,
          title: t('activate_user.toast.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('activate_user.toast.error.message'),
        },
      })
    },
  })

  const handleActivateUser = async (token: string) => {
    return await mutateAsync({ token })
  }

  return {
    handleActivateUser,
    isPendingActivateUser: isPending,
    isSuccessActivateUser: isSuccess,
    isErrorActivateUser: isError,
  }
}
